<template>
  <div class="exam-page">
    <div class="exam-container">
      <!-- 组别选择视图 -->
      <div class="exam-groups-view" v-if="!examStarted && !examFinished && !loading">
        <div class="page-header">
          <h1>模拟考核</h1>
          <p class="subtitle">选择组别开始全真模拟考试</p>
        </div>

        <div class="exam-groups">
          <div class="exam-group-card" @click="showRules('primary')">
            <div class="group-icon">🚀</div>
            <h3>小学组</h3>
            <div class="group-info">
              <div class="info-row">
                <span>题目数量：</span>
                <span>20题</span>
              </div>
              <div class="info-row">
                <span>考试时长：</span>
                <span>30分钟</span>
              </div>
              <div class="info-row">
                <span>总分：</span>
                <span>100分</span>
              </div>
            </div>
            <button class="btn btn-primary">开始考试</button>
          </div>

          <div class="exam-group-card" @click="showRules('middle')">
            <div class="group-icon">📚</div>
            <h3>初中组</h3>
            <div class="group-info">
              <div class="info-row">
                <span>题目数量：</span>
                <span>20题</span>
              </div>
              <div class="info-row">
                <span>考试时长：</span>
                <span>30分钟</span>
              </div>
              <div class="info-row">
                <span>总分：</span>
                <span>100分</span>
              </div>
            </div>
            <button class="btn btn-primary">开始考试</button>
          </div>

          <div class="exam-group-card" @click="showRules('high')">
            <div class="group-icon">🎯</div>
            <h3>高中组</h3>
            <div class="group-info">
              <div class="info-row">
                <span>题目数量：</span>
                <span>20题</span>
              </div>
              <div class="info-row">
                <span>考试时长：</span>
                <span>30分钟</span>
              </div>
              <div class="info-row">
                <span>总分：</span>
                <span>100分</span>
              </div>
            </div>
            <button class="btn btn-primary">开始考试</button>
          </div>
        </div>
      </div>

      <!-- 加载状态 -->
      <div class="loading-section" v-if="loading">
        <div class="loading-card">
          <div class="loading-icon">⏳</div>
          <h2>正在加载考试题目...</h2>
        </div>
      </div>



      <!-- 答题区域 -->
      <div class="exam-area" v-if="showExamArea">
        <div class="exam-header">
          <div class="time-display">
            <i class="fas fa-clock"></i>
            剩余时间：<strong>{{ formatTime(remainingTime) }}</strong>
          </div>
          <button class="submit-early-btn" @click="confirmSubmit">
            提交试卷
          </button>
        </div>

        <div class="question-nav">
          <div 
            v-for="(q, i) in questions" 
            :key="i"
            class="nav-dot"
            :class="{
              active: currentIndex === i,
              answered: answers[i] !== undefined && answers[i] !== ''
            }"
            @click="goToQuestion(i)"
          >
            {{ i + 1 }}
          </div>
        </div>

        <div class="question-panel" v-if="questions[currentIndex]">
          <div class="question-header">
            <span class="question-type">{{ questions[currentIndex].type || '' }}</span>
            <span class="question-score">{{ questions[currentIndex].score || 0 }}分</span>
          </div>
          
          <div class="question-title">
            <span class="q-num">Q{{ currentIndex + 1 }}.</span>
            {{ questions[currentIndex].title || '暂无题目内容' }}
          </div>

          <!-- 题目图片 -->
          <div v-if="questions[currentIndex].titleImages && questions[currentIndex].titleImages.length > 0" class="question-images">
            <img 
              v-for="(image, imgIndex) in questions[currentIndex].titleImages" 
              :key="image.id || imgIndex"
              :src="image.imageUrl"
              :alt="`题目图片 ${imgIndex + 1}`"
              class="question-image"
              @click="openImagePreview(image.imageUrl)"
              style="cursor: pointer;"
            />
          </div>

          <div class="options-list" v-if="questions[currentIndex].options">
            <div 
              v-for="opt in questions[currentIndex].options" 
              :key="opt.key"
              class="option-box"
              :class="{ selected: answers[currentIndex]?.includes(opt.key) }"
              @click="toggleOption(opt.key)"
            >
              <span class="opt-key">{{ opt.key }}</span>
              <span class="opt-text">{{ opt.text }}</span>
              <!-- 选项图片 -->
              <div v-if="opt.optionImages && opt.optionImages.length > 0" class="option-images">
                <img 
                  v-for="(img, imgIndex) in opt.optionImages" 
                  :key="img.id || imgIndex"
                  :src="img.imageUrl"
                  :alt="`选项图片 ${imgIndex + 1}`"
                  class="option-image"
                  @click="openImagePreview(img.imageUrl)"
                  style="cursor: pointer;"
                />
              </div>
            </div>
          </div>
        </div>
        <div class="question-panel" v-else>
          <div class="question-header">
            <span class="question-type">题目加载中...</span>
          </div>
          <div class="question-title">
            <span class="q-num">Q{{ currentIndex + 1 }}.</span>
            题目加载中，请稍候...
          </div>
        </div>

        <div class="exam-footer">
          <button 
            class="nav-btn prev" 
            @click="prevQuestion"
            :disabled="currentIndex === 0"
          >
            <i class="fas fa-chevron-left"></i> 上一题
          </button>
          <span class="page-indicator">{{ currentIndex + 1 }} / {{ questions.length }}</span>
          <button 
            class="nav-btn next" 
            @click="nextQuestion"
            :disabled="currentIndex === questions.length - 1"
          >
            下一题 <i class="fas fa-chevron-right"></i>
          </button>
        </div>
      </div>

      <!-- 结果区域 -->
      <div class="result-area" v-if="examFinished">
        <div class="result-icon">🎉</div>
        <h2>考试完成</h2>
        <div class="result-score">
          <span class="score-value">{{ finalScore }}</span>
          <span class="score-unit">分</span>
        </div>
        <div class="result-details">
          <div class="detail-item">
            <span class="detail-label">正确率</span>
            <span class="detail-value">{{ correctRate }}%</span>
          </div>
          <div class="detail-item">
            <span class="detail-label">用时</span>
            <span class="detail-value">{{ usedTime }}</span>
          </div>
        </div>
        <div class="result-actions">
          <button class="return-btn" @click="returnHome">返回首页</button>
        </div>
      </div>
    </div>

    <!-- 规则弹窗 -->
    <div class="modal" :class="{ active: showRulesModal }">
      <div class="modal-content">
        <h2>考试规则</h2>
        <div class="rules-content">
          <div class="rule-item">
            <span class="rule-number">1</span>
            <p>考试期间请保持网络连接稳定</p>
          </div>
          <div class="rule-item">
            <span class="rule-number">2</span>
            <p>禁止切换屏幕或打开其他网页</p>
          </div>
          <div class="rule-item">
            <span class="rule-number">3</span>
            <p>请在规定时间内完成答题</p>
          </div>
          <div class="rule-item">
            <span class="rule-number">4</span>
            <p>考试结束后系统将自动判分</p>
          </div>
          <div class="rule-item">
            <span class="rule-number">5</span>
            <p>切屏超过3次将自动交卷</p>
          </div>
        </div>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="closeRulesModal">取消</button>
          <button class="btn btn-primary" @click="confirmStartExam">确认开始</button>
        </div>
      </div>
    </div>

    <!-- 提交确认弹窗 -->
    <div class="confirm-modal" v-if="showConfirm">
      <div class="modal-content">
        <h3>确认提交</h3>
        <p>您已答完 {{ answeredCount }} / {{ questions.length }} 道题</p>
        <p class="warning">提交后将无法修改答案！</p>
        <div class="modal-buttons">
          <button class="cancel-btn" @click="showConfirm = false">取消</button>
          <button class="confirm-btn" @click="submitExam">确认提交</button>
        </div>
      </div>
    </div>

    <!-- 图片预览弹窗 -->
    <div class="image-modal" v-if="showImageModal">
      <div class="modal-content" @click="closeImagePreview">
        <button class="close-btn" @click.stop="closeImagePreview">×</button>
        <img :src="selectedImage" :alt="'预览图片'" class="preview-image" />
      </div>
    </div>
  </div>
</template>

<script>
import { startExam, submitExam } from '@/api/droneApi';
import { baseURL } from '@/api/axiosConfig';

export default {
  name: 'ExamPage',
  data() {
    return {
      examStarted: false,
      examFinished: false,
      showExamArea: false,
      showRulesModal: false,
      showConfirm: false,
      showImageModal: false,
      selectedImage: '',
      currentGroup: {},
      currentIndex: 0,
      remainingTime: 3600,
      timer: null,
      answers: {},
      questions: [],
      loading: false,
      examCode: '',
      paperName: '',
      totalCount: 0,
      totalScore: 0,
      correctCount: 0,
      finalScore: 0,
      startTime: null,
      examGroups: {
        primary: {
          name: '小学组',
          questions: 20,
          duration: 30,
          score: 100
        },
        middle: {
          name: '初中组',
          questions: 20,
          duration: 30,
          score: 100
        },
        high: {
          name: '高中组',
          questions: 20,
          duration: 30,
          score: 100
        }
      }
    }
  },
  computed: {
    answeredCount() {
      return Object.keys(this.answers).length
    },
    correctRate() {
      if (this.questions.length === 0) return 0
      let correct = 0
      this.questions.forEach((q, i) => {
        const userAnswer = this.answers[i] || []
        const correctAnswer = q.answer || []
        if (JSON.stringify(userAnswer.sort()) === JSON.stringify(correctAnswer.sort())) {
          correct++
        }
      })
      return Math.round(correct / this.questions.length * 100)
    },
    usedTime() {
      // 初始时长（秒）
      const initialDuration = this.currentGroup ? this.currentGroup.duration * 60 : 1800
      const used = initialDuration - this.remainingTime
      const mins = Math.floor(used / 60)
      const secs = used % 60
      return `${mins}分${secs}秒`
    }
  },
  methods: {
    formatTime(seconds) {
      const mins = Math.floor(seconds / 60)
      const secs = seconds % 60
      return `${mins}:${secs.toString().padStart(2, '0')}`
    },
    showRules(group) {
      this.currentGroup = this.examGroups[group]
      this.selectedGroup = group
      this.showRulesModal = true
    },
    closeRulesModal() {
      this.showRulesModal = false
    },
    async confirmStartExam() {
      this.showRulesModal = false
      this.loading = true
      try {
        const params = {
          difficulty: this.selectedGroup === 'primary' ? 1 : this.selectedGroup === 'middle' ? 2 : 3,
          count: this.currentGroup.questions
        }
        
        const res = await startExam(params)
        
        if (res.data.code === 200 && res.data.data) {
          const examData = res.data.data
          this.examCode = examData.examCode
          this.paperName = examData.paperName
          this.totalCount = examData.totalCount
          this.totalScore = examData.totalScore
          
          // 处理题目数据
          const listData = Array.isArray(examData.list) ? examData.list : []
          this.questions = listData.map(item => ({
            id: item.id,
            type: item.questionType === '0' ? '单选题' : '多选题',
            score: item.score || 5,
            title: item.questionTitle || '暂无题目内容',
            titleImages: item.titleImages ? item.titleImages.map(img => ({
              ...img,
              imageUrl: baseURL + img.imageUrl
            })) : [],
            options: item.options ? item.options.map(opt => ({
              key: opt.optionLabel || '',
              text: opt.optionContent || '',
              isCorrect: opt.isCorrect === 1,
              optionImages: opt.optionImages ? opt.optionImages.map(img => ({
                ...img,
                imageUrl: baseURL + img.imageUrl
              })) : []
            })) : [],
            answer: item.options ? item.options
              .filter(opt => opt.isCorrect === 1)
              .map(opt => opt.optionLabel) : []
          }))
          
          this.examStarted = true
          this.showExamArea = true
          this.remainingTime = this.currentGroup.duration * 60
          this.startTimer()
        } else {
          alert('获取考试题目失败，请稍后重试')
          this.showRulesModal = true
        }
      } catch (error) {
        console.error('开始考试失败:', error)
        alert('获取考试题目失败，请稍后重试')
        this.showRulesModal = true
      } finally {
        this.loading = false
      }
    },
    startTimer() {
      // 记录开始时间
      this.startTime = new Date()
      this.timer = setInterval(() => {
        if (this.remainingTime > 0) {
          this.remainingTime--
        } else {
          this.submitExam()
        }
      }, 1000)
    },
    goToQuestion(index) {
      this.currentIndex = index
    },
    toggleOption(key) {
      const q = this.questions[this.currentIndex]
      if (!this.answers[this.currentIndex]) {
        this.answers[this.currentIndex] = []
      }
      if (q.type === '多选题') {
        const idx = this.answers[this.currentIndex].indexOf(key)
        if (idx > -1) {
          this.answers[this.currentIndex].splice(idx, 1)
        } else {
          this.answers[this.currentIndex].push(key)
        }
      } else {
        this.answers[this.currentIndex] = [key]
      }
    },
    prevQuestion() {
      if (this.currentIndex > 0) this.currentIndex--
    },
    nextQuestion() {
      if (this.currentIndex < this.questions.length - 1) this.currentIndex++
    },
    confirmSubmit() {
      this.showConfirm = true
    },
    async submitExam() {
      clearInterval(this.timer)
      this.loading = true
      try {
        // 检查 examCode 是否为空
        if (!this.examCode) {
          alert('考试信息加载失败，请重新开始考试')
          this.loading = false
          return
        }
        
        // 构建答案数组
        const answers = this.questions.map((question, index) => {
          const userAnswer = this.answers[index] || []
          return {
            questionId: question.id,
            answer: userAnswer.join(',')
          }
        })
        
        // 计算实际用时（几分几秒）
        let duration = '0分0秒'
        if (this.startTime) {
          const endTime = new Date()
          const timeDiff = endTime - this.startTime
          const totalSeconds = Math.floor(timeDiff / 1000)
          const mins = Math.floor(totalSeconds / 60)
          const secs = totalSeconds % 60
          duration = `${mins}分${secs}秒`
        }
        
        // 确定学段（difficulty）
        let difficulty = 1 // 默认小学
        if (this.selectedGroup === 'middle') {
          difficulty = 2 // 初中
        } else if (this.selectedGroup === 'high') {
          difficulty = 3 // 高中
        }
        
        // 构建请求数据
        const requestData = {
          examCode: this.examCode,
          answers: answers,
          duration: duration,
          difficulty: difficulty
        }
        
        // 调用接口提交答案
        const res = await submitExam(requestData)
        
        if (res.data.code === 200 && res.data.data) {
          const resultData = res.data.data
          // 更新结果数据
          this.finalScore = resultData.total_score || resultData.userScore || 0
          this.correctCount = resultData.correctCount || 0
          this.totalCount = resultData.totalCount || this.questions.length
          this.examFinished = true
          this.showExamArea = false
          this.showConfirm = false
        } else {
          alert('提交答案失败，请稍后重试')
        }
      } catch (error) {
        console.error('提交答案失败:', error)
        alert('提交答案失败，请稍后重试')
      } finally {
        this.loading = false
      }
    },
    reviewExam() {
      console.log('查看答案')
    },
    returnHome() {
      this.examStarted = false
      this.examFinished = false
      this.showExamArea = false
      this.answers = {}
      this.currentIndex = 0
      this.remainingTime = 3600
    },
    
    // 打开图片预览
    openImagePreview(imageUrl) {
      this.selectedImage = imageUrl
      this.showImageModal = true
    },
    
    // 关闭图片预览
    closeImagePreview() {
      this.showImageModal = false
      this.selectedImage = ''
    }
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer)
  }
}
</script>

<style scoped>
.exam-page {
  padding: 20px;
  min-height: calc(100vh - 200px);
}

.exam-container {
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  text-align: center;
  margin-bottom: 40px;
}

.page-header h1 {
  font-size: 32px;
  color: #1a5f7a;
  margin-bottom: 10px;
}

.subtitle {
  color: #666;
  font-size: 16px;
}

/* 组别卡片样式 */
.exam-groups {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-top: 30px;
}

.exam-group-card {
  background: white;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  box-shadow: 0 4px 16px rgba(0,0,0,0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  border: 2px solid transparent;
}

.exam-group-card:hover {
  transform: translateY(-5px);
  border-color: #1a5f7a;
  box-shadow: 0 8px 24px rgba(26, 95, 122, 0.2);
}

.group-icon {
  font-size: 50px;
  margin-bottom: 15px;
}

.exam-group-card h3 {
  color: #1a5f7a;
  font-size: 22px;
  margin-bottom: 20px;
}

.group-info {
  text-align: left;
  margin-bottom: 25px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 10px;
}

.info-row {
  display: flex;
  justify-content: space-between;
  padding: 8px 0;
  border-bottom: 1px solid #eee;
}

.info-row:last-child {
  border-bottom: none;
}

.info-row span:first-child {
  color: #666;
}

.info-row span:last-child {
  font-weight: bold;
  color: #333;
}

.btn {
  padding: 12px 30px;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  width: 100%;
}

.btn-primary {
  background: linear-gradient(135deg, #1a5f7a, #2d8fb3);
  color: white;
}

.btn-primary:hover {
  background: linear-gradient(135deg, #155269, #2579a3);
}

.btn-secondary {
  background: #e0e0e0;
  color: #333;
}

.btn-secondary:hover {
  background: #d0d0d0;
}

/* 考试须知 */
.exam-notice {
  background: white;
  border-radius: 16px;
  padding: 40px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.notice-icon {
  font-size: 60px;
  margin-bottom: 20px;
}

.exam-notice h2 {
  color: #1a5f7a;
  margin-bottom: 20px;
}

.exam-notice ul {
  text-align: left;
  max-width: 500px;
  margin: 0 auto 30px;
  color: #666;
}

.exam-notice li {
  margin: 10px 0;
  padding-left: 10px;
}

.student-info {
  display: flex;
  justify-content: center;
  gap: 40px;
  margin-bottom: 30px;
  padding: 20px;
  background: #f8f9fa;
  border-radius: 8px;
}

.info-item .label {
  color: #666;
}

.info-item .value {
  font-weight: bold;
  color: #333;
}

.start-exam-btn {
  padding: 15px 50px;
  background: linear-gradient(135deg, #1a5f7a, #2d8fb3);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 18px;
  cursor: pointer;
  transition: all 0.3s;
}

.start-exam-btn:hover {
  background: linear-gradient(135deg, #155269, #2579a3);
  transform: scale(1.05);
}

/* 答题区域 */
.exam-area {
  background: white;
  border-radius: 16px;
  padding: 30px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.exam-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 20px;
  border-bottom: 1px solid #eee;
}

.time-display {
  font-size: 18px;
  color: #e74c3c;
}

.time-display strong {
  font-size: 24px;
}

.submit-early-btn {
  padding: 10px 20px;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.3s;
}

.submit-early-btn:hover {
  background: #219a52;
}

.question-nav {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-bottom: 20px;
  padding: 15px;
  background: #f8f9fa;
  border-radius: 8px;
}

.nav-dot {
  width: 32px;
  height: 32px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #e0e0e0;
  border-radius: 50%;
  font-size: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.nav-dot:hover {
  background: #d0d0d0;
}

.nav-dot.active {
  background: #1a5f7a;
  color: white;
}

.nav-dot.answered {
  background: #27ae60;
  color: white;
}

.question-panel {
  margin-bottom: 20px;
}

.question-header {
  display: flex;
  gap: 15px;
  margin-bottom: 15px;
}

.question-type {
  padding: 5px 12px;
  background: #e8f4f8;
  color: #1a5f7a;
  border-radius: 4px;
  font-size: 14px;
}

.question-score {
  padding: 5px 12px;
  background: #fff3cd;
  color: #856404;
  border-radius: 4px;
  font-size: 14px;
}

.question-title {
  font-size: 18px;
  margin-bottom: 20px;
  line-height: 1.6;
}

/* 题目图片 */
.question-images {
  margin: 20px 0;
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.question-image {
  max-width: 100%;
  max-height: 200px;
  border-radius: 6px;
  box-shadow: 0 2px 4px rgba(0,0,0,0.1);
}

/* 选项图片 */
.option-images {
  margin-top: 10px;
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  margin-left: 40px;
}

.option-image {
  max-width: 100px;
  max-height: 100px;
  border-radius: 4px;
  box-shadow: 0 1px 3px rgba(0,0,0,0.1);
}

.q-num {
  font-weight: bold;
  color: #1a5f7a;
}

.options-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.option-box {
  display: flex;
  align-items: flex-start;
  padding: 15px;
  border: 2px solid #e0e0e0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
}

.option-box:hover {
  border-color: #1a5f7a;
  background: #f8f9fa;
}

.option-box.selected {
  border-color: #1a5f7a;
  background: #e8f4f8;
}

.opt-key {
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #f0f0f0;
  border-radius: 50%;
  font-weight: bold;
  margin-right: 12px;
  flex-shrink: 0;
}

.option-box.selected .opt-key {
  background: #1a5f7a;
  color: white;
}

.opt-text {
  flex: 1;
  line-height: 1.5;
}

.exam-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding-top: 20px;
  border-top: 1px solid #eee;
}

.nav-btn {
  padding: 12px 25px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s;
}

.nav-btn.prev {
  background: #e0e0e0;
  color: #333;
}

.nav-btn.prev:hover:not(:disabled) {
  background: #d0d0d0;
}

.nav-btn.next {
  background: #1a5f7a;
  color: white;
}

.nav-btn.next:hover:not(:disabled) {
  background: #155269;
}

.nav-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.page-indicator {
  color: #666;
  font-size: 16px;
}

/* 结果区域 */
.result-area {
  background: white;
  border-radius: 16px;
  padding: 50px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.result-icon {
  font-size: 80px;
  margin-bottom: 20px;
}

.result-score {
  margin: 30px 0;
}

.score-value {
  font-size: 72px;
  font-weight: bold;
  color: #1a5f7a;
}

.score-unit {
  font-size: 24px;
  color: #666;
}

.result-details {
  display: flex;
  justify-content: center;
  gap: 50px;
  margin-bottom: 30px;
}

.detail-item {
  text-align: center;
}

.detail-label {
  display: block;
  color: #666;
  margin-bottom: 8px;
}

.detail-value {
  font-size: 24px;
  font-weight: bold;
  color: #333;
}

.result-actions {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.return-btn {
  padding: 12px 30px;
  border: none;
  border-radius: 6px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.3s;
  background: #e0e0e0;
  color: #333;
}

.return-btn:hover {
  background: #d0d0d0;
}

/* 规则弹窗 */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  opacity: 0;
  visibility: hidden;
  transition: all 0.3s;
}

.modal.active {
  opacity: 1;
  visibility: visible;
}

.modal .modal-content {
  background: white;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  max-width: 450px;
  width: 90%;
  transform: scale(0.9);
  transition: all 0.3s;
}

.modal.active .modal-content {
  transform: scale(1);
}

.modal-content h2 {
  color: #1a5f7a;
  margin-bottom: 25px;
  font-size: 24px;
}

.rules-content {
  text-align: left;
  margin-bottom: 30px;
}

.rule-item {
  display: flex;
  align-items: flex-start;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
}

.rule-item:last-child {
  border-bottom: none;
}

.rule-number {
  width: 28px;
  height: 28px;
  background: #1a5f7a;
  color: white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  margin-right: 15px;
  flex-shrink: 0;
}

.rule-item p {
  flex: 1;
  color: #333;
  line-height: 1.5;
  margin: 0;
}

.modal-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
}

.modal-actions .btn {
  width: auto;
  padding: 12px 35px;
}

/* 提交确认弹窗 */
.confirm-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.confirm-modal .modal-content {
  background: white;
  border-radius: 16px;
  padding: 30px;
  text-align: center;
  max-width: 400px;
  width: 90%;
}

.confirm-modal .modal-content h3 {
  margin-bottom: 15px;
  color: #333;
}

.confirm-modal .warning {
  color: #e74c3c;
  font-weight: bold;
  margin: 15px 0;
}

.modal-buttons {
  display: flex;
  gap: 15px;
  justify-content: center;
  margin-top: 20px;
}

.cancel-btn, .confirm-btn {
  padding: 10px 25px;
  border: none;
  border-radius: 6px;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.3s;
}

.cancel-btn {
  background: #e0e0e0;
  color: #333;
}

.cancel-btn:hover {
  background: #d0d0d0;
}

.confirm-btn {
  background: #e74c3c;
  color: white;
}

.confirm-btn:hover {
  background: #c0392b;
}

/* 响应式 */
@media (max-width: 768px) {
  .exam-groups {
    grid-template-columns: 1fr;
    gap: 20px;
  }
  
  .page-header h1 {
    font-size: 26px;
  }
  
  .exam-group-card {
    padding: 25px;
  }
  
  .result-details {
    flex-direction: column;
    gap: 20px;
  }
}

/* 加载状态 */
.loading-section {
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 400px;
}

.loading-card {
  background: white;
  border-radius: 16px;
  padding: 50px;
  text-align: center;
  box-shadow: 0 4px 12px rgba(0,0,0,0.1);
}

.loading-icon {
  font-size: 80px;
  margin-bottom: 20px;
  animation: pulse 1.5s ease-in-out infinite;
}

@keyframes pulse {
  0%, 100% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.1);
  }
}

.loading-card h2 {
  color: #1a5f7a;
  margin-bottom: 15px;
}

/* 图片预览模态框 */
.image-modal {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0,0,0,0.9);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1001;
  padding: 20px;
}

.image-modal .modal-content {
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
  background: transparent;
  border-radius: 0;
  padding: 0;
}

.image-modal .close-btn {
  position: absolute;
  top: -40px;
  right: -40px;
  width: 40px;
  height: 40px;
  background: white;
  border: none;
  border-radius: 50%;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10;
}

.image-modal .close-btn:hover {
  background: #f0f0f0;
}

.preview-image {
  max-width: 100%;
  max-height: 90vh;
  object-fit: contain;
  border-radius: 8px;
}

@media (max-width: 768px) {
  .image-modal .close-btn {
    top: 10px;
    right: 10px;
    width: 30px;
    height: 30px;
    font-size: 20px;
  }
}
</style>
